/**
 * iter122 — Data Confidence pill.
 *
 * Small always-on badge on /trade-doctor and /dashboard showing the
 * user's overall data-quality level (green / yellow / red). On
 * hover (desktop) or tap (mobile), reveals a per-source breakdown
 * card with plain-English reasons, affected analytics, and — when
 * fix_action="reprocess" — a "Fix now" button that hits
 * /api/delta-connection/reprocess.
 *
 * Design tenets:
 *  • Never colour-only — every pill has an icon (● green / ▲ yellow /
 *    ⚠ red) AND a text label. Color-blind safe.
 *  • Copy for YELLOW codes is constructive, not alarming — LOW_SAMPLE
 *    should read like "still building your track record", not "warning".
 *  • Copy for RED codes is direct + actionable — always paired with a
 *    concrete fix.
 *  • Zero-noise: renders NOTHING when there's no data
 *    (per_source empty AND trade_count = 0).
 */
import React from "react";
import { CheckCircle2, TriangleAlert, ShieldAlert, Loader2, Wrench } from "lucide-react";
import api from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { toast } from "sonner";

// ── Copy overrides — friendly-first ────────────────────────────────
// The backend's `message` field is technically precise but too clinical
// for a user-facing badge. We override per-code where a warmer tone
// helps (esp. yellow codes that describe expected states, not bugs).
// Falls back to the raw server message for anything we don't override.
const FRIENDLY_MESSAGES = {
  // ── Green-friendly yellow codes ──
  LOW_SAMPLE: {
    headline: "Still building your track record",
    detail: "Ratios like win-rate and expectancy get more reliable past ~10 closed trades. Perfectly normal for a new account.",
  },
  FUNDING_UNAVAILABLE: {
    headline: "Perpetual funding not attributed yet",
    detail: "Your net P&L reflects trading fees but not the funding cost of holding overnight perp positions. Reprocess (or wait for the next 15-min sync) to backfill it.",
  },
  // ── Actionable yellow ──
  RECONCILIATION_GAP: {
    headline: "P&L numbers don't quite tie out",
    detail: "The sum of recorded net P&L differs from (gross − fees) by more than $0.50. Reprocess to refresh from the raw fills.",
  },
  // ── Red codes ──
  UNRESOLVED_DUPES: {
    headline: "Duplicate fills detected in your journal",
    detail: "Some trades appear more than once — your win-rate and trade count are currently inflated. Reprocess to clean this up.",
  },
  POSITION_IMBALANCE: {
    headline: "Some fill legs aren't paired",
    detail: "Total buy quantity doesn't match total sell quantity for one or more symbols. Reprocess to try pairing again with the latest fills.",
  },
  STALE_REBUILD_VERSION: {
    headline: "Trades derived by an older version",
    detail: "Some trades were rebuilt before the current pipeline upgrade. Reprocess to bring them current so analytics use consistent logic.",
  },
  MULTIPLIER_MISSING: {
    headline: "Contract multiplier missing",
    detail: "One or more Delta symbols couldn't be resolved to their contract size — quantities on those trades may be off. Contact support with the symbol so we can add it.",
  },
};

// Human-readable labels for `affected_analytics` codes
const ANALYTIC_LABELS = {
  win_rate: "Win rate",
  trade_count: "Trade count",
  expectancy: "Expectancy",
  profit_factor: "Profit factor",
  avg_win: "Avg win",
  avg_loss: "Avg loss",
  max_drawdown: "Max drawdown",
  net_pnl_absolute: "Net P&L",
  all: "All analytics",
};

// ── Level-driven visuals ───────────────────────────────────────────
const LEVEL_STYLE = {
  green: {
    label: "Data green",
    Icon: CheckCircle2,
    fg: "var(--bt-accent)",
    bg: "color-mix(in srgb, var(--bt-accent) 14%, var(--bt-surface))",
    border: "color-mix(in srgb, var(--bt-accent) 50%, var(--bt-border))",
  },
  yellow: {
    label: "Data yellow",
    Icon: TriangleAlert,
    fg: "#ff9500",
    bg: "color-mix(in srgb, #ff9500 12%, var(--bt-surface))",
    border: "color-mix(in srgb, #ff9500 50%, var(--bt-border))",
  },
  red: {
    label: "Data red",
    Icon: ShieldAlert,
    fg: "var(--bt-danger)",
    bg: "color-mix(in srgb, var(--bt-danger) 14%, var(--bt-surface))",
    border: "color-mix(in srgb, var(--bt-danger) 55%, var(--bt-border))",
  },
};

const SOURCE_LABEL = {
  delta_api: "Delta Exchange (live sync)",
  csv: "CSV imports",
  manual: "Manually logged",
};

function friendly(reason) {
  const f = FRIENDLY_MESSAGES[reason.code];
  return f || { headline: reason.message, detail: null };
}

function affectedLabel(codes) {
  if (!Array.isArray(codes) || codes.length === 0) return null;
  if (codes.includes("all")) return "Every analytic";
  return codes.map((c) => ANALYTIC_LABELS[c] || c).join(" · ");
}

export function DataConfidencePill({ testid = "data-confidence-pill", compact = false }) {
  const [state, setState] = React.useState({ loading: true, data: null });
  const [reprocessing, setReprocessing] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/trade-doctor/data-confidence");
      setState({ loading: false, data });
    } catch (e) {
      // Silent — the pill is a nice-to-have, never blocks the page.
      setState({ loading: false, data: null });
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const onReprocess = async () => {
    setReprocessing(true);
    try {
      const { data } = await api.post("/delta-connection/reprocess");
      if (data?.status === "locked") {
        toast.info("A sync is running — try again in a minute.");
      } else if (typeof data?.trades_created === "number") {
        toast.success(`Reprocessed — your journal now shows ${data.trades_created} trade${data.trades_created === 1 ? "" : "s"} from Delta.`);
        await load();
      } else {
        toast.success("Reprocess complete.");
        await load();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't reprocess right now — please try again.";
      toast.error(msg);
    } finally {
      setReprocessing(false);
    }
  };

  // ── Loading ──
  if (state.loading) {
    return (
      <span
        data-testid={`${testid}-loading`}
        className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs"
        style={{ borderColor: "var(--bt-border)", color: "var(--bt-text-muted)" }}
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        Data quality
      </span>
    );
  }

  // ── No data / no signal — render nothing ──
  const d = state.data;
  if (!d || !d.per_source || d.trade_count === 0) return null;

  const level = d.overall_level || "green";
  const style = LEVEL_STYLE[level] || LEVEL_STYLE.green;
  const { Icon } = style;

  // Aggregate per-source reasons for the popover
  const sources = Object.entries(d.per_source).filter(
    ([, info]) => (info?.trade_count ?? 0) > 0
  );

  const anyReprocessAction = sources.some(([, info]) =>
    info?.fix_action === "reprocess"
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          data-level={level}
          className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold whitespace-nowrap transition-transform hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--bt-accent)]"
          style={{ color: style.fg, background: style.bg, borderColor: style.border }}
        >
          <Icon className="w-3.5 h-3.5" aria-hidden="true" />
          <span data-testid={`${testid}-label`}>
            {compact ? style.label.replace("Data ", "") : style.label}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[min(92vw,420px)] p-0 border"
        style={{ background: "var(--bt-surface)", borderColor: "var(--bt-border)" }}
        data-testid={`${testid}-card`}
      >
        <div className="p-3 sm:p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[11px] tracking-[0.14em] uppercase text-[var(--bt-text-muted)]">Data Confidence</div>
              <div className="text-sm font-semibold" style={{ color: style.fg }}>
                {level === "green" && "Everything looks solid"}
                {level === "yellow" && "Usable with a few caveats"}
                {level === "red" && "A couple of things need your attention"}
              </div>
            </div>
            {level === "red" && anyReprocessAction && (
              <button
                type="button"
                data-testid={`${testid}-fix-now-btn`}
                disabled={reprocessing}
                onClick={onReprocess}
                className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold border transition-opacity disabled:opacity-60"
                style={{
                  color: "#ffffff",
                  background: "#E0364C",
                  borderColor: "transparent",
                }}
              >
                {reprocessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />}
                Fix now
              </button>
            )}
          </div>

          {sources.length === 0 && (
            <p className="text-xs text-[var(--bt-text-muted)]">
              No trade sources active yet. Sync Delta or import a CSV to see per-source signals.
            </p>
          )}

          <div className="space-y-3">
            {sources.map(([source, info]) => {
              const srcLevel = info?.level || "green";
              const srcStyle = LEVEL_STYLE[srcLevel] || LEVEL_STYLE.green;
              const SrcIcon = srcStyle.Icon;
              const reasons = info?.reasons || [];
              return (
                <div
                  key={source}
                  data-testid={`${testid}-source-${source}`}
                  className="rounded-md border p-2.5"
                  style={{ borderColor: "var(--bt-border)" }}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="text-xs font-semibold text-[var(--bt-text)]">
                      {SOURCE_LABEL[source] || source}
                    </div>
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase border"
                      style={{ color: srcStyle.fg, background: srcStyle.bg, borderColor: srcStyle.border }}
                    >
                      <SrcIcon className="w-3 h-3" aria-hidden="true" />
                      {srcLevel}
                    </span>
                  </div>
                  <div className="text-[11px] text-[var(--bt-text-muted)] mb-1.5">
                    {info?.trade_count ?? 0} trade{info?.trade_count === 1 ? "" : "s"}
                  </div>

                  {reasons.length === 0 ? (
                    <p className="text-xs text-[var(--bt-text-muted)]">
                      Nothing to flag — analytics from this source are trustworthy.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {reasons.map((r, i) => {
                        const f = friendly(r);
                        return (
                          <li key={`${r.code}-${i}`} data-testid={`${testid}-reason-${r.code}`}>
                            <div className="text-xs font-medium text-[var(--bt-text)]">
                              {f.headline}
                            </div>
                            {f.detail && (
                              <div className="text-[11px] text-[var(--bt-text-muted)] leading-relaxed mt-0.5">
                                {f.detail}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {/* Source-level: affected analytics + fix hint */}
                  {(() => {
                    const affected = affectedLabel(info?.affected_analytics);
                    if (!affected) return null;
                    return (
                      <div className="text-[10px] text-[var(--bt-text-muted)] mt-2 pt-2 border-t"
                           style={{ borderColor: "var(--bt-border)" }}>
                        <span className="tracking-wider uppercase mr-1">Affects:</span>
                        {affected}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>

          <div className="text-[10px] text-[var(--bt-text-muted)] pt-1 border-t" style={{ borderColor: "var(--bt-border)" }}>
            Updated {d.computed_at ? new Date(d.computed_at).toLocaleTimeString() : "just now"}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DataConfidencePill;
